/**
 * The serving surface — §4's own section divider, and the two tables under it: which
 * hostnames a tenant may be served on (`domains`), and how a consumer page is themed
 * (`page_templates`).
 *
 * Same three-part treatment as every other enumerated column in this schema (ruled
 * 2026-08-04): `text` + `CHECK` in SQL, the constant here, and `constraints.test.ts`
 * asserting the two are set-equal in both directions. A member added to one side alone
 * is red.
 */
/**
 * What a custom hostname is *for* — `domains.kind`.
 *
 * The two rails are separate rows even when a tenant points both at us, because they
 * are different delegations answering different questions: `resolver` is the CNAME to
 * `pkgedge.com` that printed Digital Link URIs resolve through (§7), and `pip` is the
 * host a consumer page is served on. A single row carrying both would make
 * "is the resolver live?" and "is the consumer page live?" one fact, and §8.1's hard
 * block is asked of the first only.
 */
export const DOMAIN_KINDS = ["resolver", "pip"];
/**
 * Where a delegation has got to — `domains.status`.
 *
 * The ladder §7 describes: the tenant adds the CNAME (`pending_dns`), DNS resolves to
 * our edge (`verifying`), the first real TLS handshake issues a certificate and a
 * synthetic probe answers 307 (`live`), and anything that stops working lands in
 * `error`. **`error` is a statement about now, never about history** — `first_live_at`
 * carries the history and is never cleared, which is the distinction §8.1's two rows
 * turn on.
 *
 * From U12 the recheck path moves `pending_dns`/`error` → `verifying` off a DoH answer.
 * `live` is still written by nothing: the synthetic probe and the stamping worker are
 * U13's, so §8.1 gate 1 blocks every custom-domain artwork request until they land.
 *
 * `removed` is what "delete a domain" means here, ruled 2026-09-08. `0016` grants DELETE
 * to nobody, because DELETE plus INSERT clears `first_live_at` and routes around the
 * write-once trigger. So removal is a transition, the row and its history survive, and
 * re-adding the same hostname revives that row.
 */
export const DOMAIN_STATUSES = [
    "pending_dns",
    "verifying",
    "live",
    "error",
    "removed",
];
/**
 * The status meaning "the tenant took this hostname out of service".
 *
 * Deliberately absent from {@link ASK_ELIGIBLE_STATUSES}: a removed row must not answer
 * the ask. The mirror never sees this value at all — removal emits the outbox `delete`
 * action, so the poller drops the row and an unknown host is refused.
 */
// `satisfies` rather than a `: DomainStatus` annotation, so the constant keeps its LITERAL
// type: an annotation widens it to the whole union, and a comparison against a union-typed
// value narrows nothing — a caller switching on it then has to spell "removed" a second
// time, which is the one thing naming a constant is meant to prevent.
export const REMOVED_DOMAIN_STATUS = "removed";
/**
 * Every status except {@link REMOVED_DOMAIN_STATUS}, derived rather than spelled.
 *
 * One place answers "is this row still the tenant's". A second literal list would be a
 * second thing to remember when a status is added.
 */
export const ACTIVE_DOMAIN_STATUSES = DOMAIN_STATUSES.filter((status) => status !== REMOVED_DOMAIN_STATUS);
/**
 * The one `kind` a consumer page is served on, named rather than spelled at each use.
 *
 * `buildEntry`'s `customPip` predicate and §8.1's gate both compare against it, and a
 * literal in either place is a second copy of a vocabulary this file exists to hold
 * once. Typed as {@link DomainKind}, so renaming the member is a compile error here
 * rather than a silently-false `WHERE` clause two packages away.
 */
export const PIP_DOMAIN_KIND = "pip";
/**
 * The one `status` that means a delegation is answering right now.
 *
 * Same reasoning as {@link PIP_DOMAIN_KIND}. Note what this is **not**: the artwork
 * gate (§8.1 row 1) reads `first_live_at IS NULL`, not this value — a domain live for
 * six months and momentarily in `error` has a non-NULL `first_live_at` and is a
 * warn-plus-typed-confirm, never a block. The two questions are *"has this delegation
 * ever worked?"* and *"is it working now?"*, and §7 is explicit that they are different.
 */
export const LIVE_DOMAIN_STATUS = "live";
/**
 * The statuses `/internal/tls-check` answers 200 for — Caddy's `ask` (§7, TR).
 *
 * §7 and the Phase 4 plan both say *"status ≥ `verifying`"*, and **that ordering does
 * not exist**: `error` sits after `live` in {@link DOMAIN_STATUSES}, so an index
 * comparison would issue certificates for broken delegations. A set, never a rung.
 */
export const ASK_ELIGIBLE_STATUSES = ["verifying", "live"];
/**
 * How many consecutive failed watch probes make a failure *sustained* — §11's *"beyond a
 * short grace"*, given a number.
 *
 * §11 says *"within hours"* and names no figure. The measured single-probe false negatives
 * are ≤ 10 s events — a Caddy recreate at 1.302 s, an issuance window at 5.42–9.19 s — so
 * at an hourly interval `2` already excludes them by more than 300×. `3` additionally
 * tolerates two independent short events an hour apart, costs one more hour before the
 * banner, and stays inside *"within hours"*. Fable's re-plan §3, 2026-09-15.
 */
export const SUSTAINED_PROBE_FAILURES = 3;
/**
 * The CNAME target every tenant points `id.brand.com` at — `screens.md` §8's
 * non-negotiable copy, and TR §7's DNS UX paragraph.
 *
 * A separate permanent registration, so a rebrand of `packagepage.com` touches no
 * customer's DNS. Two consumers: the instruction card's VALUE row, and the recheck that
 * compares a DoH answer against it.
 */
export const EDGE_CNAME_TARGET = "edge.pkgedge.com";
/**
 * The consumer-page layouts — `page_templates.layout`.
 *
 * **Three, and the design handoff says four.** `screens.md` §12 reads *"Four layouts:"*
 * and then lists these three plus a per-product `layout-override`, which is an override
 * rather than a layout; §4's enum and the Step 0 plan both say three. Recorded in the
 * Phase 3 Drift Log as the sixth self-miscount in that package — this constant is the
 * parser-countable number, and a test written to "four" would be red against correct
 * work.
 *
 * `compliance` is the EU wine e-label view, in which marketing and sales content is
 * structurally excluded rather than merely absent (Phase 3 U9). It is **not sold as
 * compliant** — §12.1's lawyer-opinion caveat stands.
 */
export const PAGE_TEMPLATE_LAYOUTS = ["classic", "hero", "compliance"];
//# sourceMappingURL=serving.js.map